import React, { useMemo, useState } from 'react'
import { RiErrorWarningFill } from "react-icons/ri";
import styles from "../css/SignUp.module.css"
import { IoMailOutline } from "react-icons/io5";
import { CgPassword } from "react-icons/cg";
import { IoEyeOffOutline } from "react-icons/io5";
import { IoEyeOutline } from "react-icons/io5";
import { IoWarning } from "react-icons/io5";
import { LuUser } from "react-icons/lu";
import { IoMdCloseCircle } from "react-icons/io";
import LoadingButton from '../../Loaders/js/LoadingButton';
import { NavLink, useNavigate } from 'react-router-dom';
import validator from "validator"
import { useDispatch, useSelector } from 'react-redux';
import { signup } from '../../../actions/authActions';
import { useEffect } from 'react';
import { AUTH_RESET } from '../../../actions/authTypes';
import AuthScreen from './AuthScreen';
import { SignUpFloatingCards } from './FloatingCards';
import MobileHeader from './MobileHeader';
import LogoItem from '../../Logo/LogoItem';

const SignUp = () => {
  const [data,setData] = useState({fullname:"",email:"",password:"", confirm_password: ""})
  const [showPassword,setShowPassword] = useState(false)
  const [crashError,setCrashError] = useState("")
  const [errors,setErrors] = useState({})
  const loading = useSelector((state) => state.authState.loading)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  useEffect(()=>{
    return () => {
      dispatch({type: AUTH_RESET})
    }
  },[dispatch])

  const handleSubmit = async(e) => {
    setCrashError("")
    e.preventDefault()
    let valid = true
    if (data.password.length < 8){
      valid = false
      setErrors((prevErrors)=> {return {...prevErrors,password:"At least 8 characters"}})
    }
    if (data.confirm_password !== data.password){
      valid = false
      setErrors((prevErrors)=> {return {...prevErrors,confirm_password:"Passwords do not match"}})
    }
    if (!validator.isEmail(data.email)){
      valid = false
      setErrors((prevErrors)=> {return {...prevErrors,email:"Invalid email address"}})
    }
    if (valid){
      try{
        await dispatch(signup({fullname: data.fullname,email: data.email,password: data.password}))
        navigate("/notes")
      }
      catch(error){
        if (error?.response?.status === 400){
          setErrors({...errors,...error.response.data})
        }
        else{
          setCrashError("Something unexpected occured")
        }
      }
    }
  }

    const handleKeyDown = (e) => {
        const isPrintableCharacter = e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey
        if (isPrintableCharacter){
            if (data.fullname.length === 25){
                if (e.target?.parentNode?.parentNode){
                  e.target.parentNode.parentNode.classList.add(styles.shake)
                }
                setTimeout(()=>{
                if (e.target?.parentNode?.parentNode){
                  e.target.parentNode.parentNode.classList.remove(styles.shake)
                }
                },300)
            }
        }
    }

  const handleChange = (e) => {
    let name = e.target.name
    let value = e.target.value
    setData({...data,[name]:value})
    if (name === "email" && errors.email){
      if (errors.email === "Invalid email address"){
        if (validator.isEmail(value) || !value) setErrors({...errors,email:""})
      }
      else{
        setErrors({...errors,email:""})
      }
    }
    else if (name === "password"){
      if (value.length >= 8 && errors.password) setErrors((prevErrors)=>{return {...prevErrors,password:""}})
      if (errors.confirm_password && data.confirm_password === value) setErrors((prevErrors)=>{return {...prevErrors,confirm_password:""}})
    }
    else if (name === "confirm_password" && errors.confirm_password && (value === data.password || !value)){
      setErrors({...errors,confirm_password:""})
    }
  }

  const clearError = () => {
      setCrashError("")
  }

  const isDisabled = useMemo(() => {
    if (loading) return true
    if (!data.fullname.trim() || !data.email.trim() || !data.password){
      return true
    }
    if (Object.values(errors).some(val => val !== "")){
      return true
    }
    else{
      return false
    }
  },[errors,data,loading])

  return (
    <AuthScreen 
      form = {
        <div className = {styles.box}>
          <MobileHeader title = "Sign up" subtitle = "Fill in your information to join us"/>
          <div className = {styles.topNav}>
            <div className = {styles.appRep}><LogoItem/></div>
            <p className = {styles.navLink}>Already have an account? <NavLink to = "/login">Sign in</NavLink></p>
          </div>
          <main>
            <div className = {styles.title}>
              <div className = {styles.mainTitle}>Get Started</div>
              <div className = {styles.instructions}>Fill in your information to join us</div>
            </div>
            <form onSubmit = {handleSubmit} autoComplete = "off">
              {crashError && <div data-testid = "crash-error" className = {styles.error}>
                <div className = {styles.errorInfo}>
                  <div className = {styles.warningIcon}><IoWarning/></div>
                  {crashError}
                </div>
                <div className = {styles.closeIcon} onClick = {()=>clearError()}><IoMdCloseCircle/></div>
              </div>}
              <div className = {styles.inputs}>
                <div className = {styles.inputWrapper}>
                  <label 
                    htmlFor = "fullname"
                  >
                    Full name
                  </label>
                  <div className= {styles.inputBox}>
                    <div className = {styles.inputIcon}><LuUser/></div>
                    <div className = {styles.inputContainer}>
                      <input
                        type = "text"
                        value = {data.fullname}
                        name = "fullname"
                        onChange = {handleChange}
                        placeholder = "Enter your name"
                        autoComplete = "off"
                        maxLength = {25}
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                  </div>
                </div>
                <div className = {styles.inputWrapper}>
                  <label 
                    htmlFor = "email"
                  >
                    Email
                  </label>
                  <div className= {`${styles.inputBox} ${errors.email ? styles.errorBox : ""}`}>
                    {errors.email ? <div className = {styles.errorIcon}><RiErrorWarningFill/></div> : <div className = {styles.inputIcon}><IoMailOutline/></div>}
                    <div className = {styles.inputContainer}>
                      <input
                        type = "text"
                        value = {data.email}
                        name = "email"
                        onChange = {handleChange}
                        placeholder = "Enter your email"
                        autoComplete = "off"
                      />
                    </div>
                  </div>
                  {errors.email && <div data-testid = "email-error" className = {styles.inputError}>{errors.email}</div>}
                </div>
                <div className = {styles.inputWrapper}>
                  <label 
                    htmlFor = "password"
                  >
                    Password
                  </label>
                  <div className={`${styles.inputBox} ${errors.password ? styles.errorBox : ""}`}>
                  {errors.password ? <div className = {styles.errorIcon}><RiErrorWarningFill/></div> : <div className = {styles.inputIcon}><CgPassword/></div>}
                    <div className = {styles.inputContainer}>
                      <input
                        type = {showPassword ? "text" : "password"}
                        value = {data.password}
                        name = "password"
                        onChange = {handleChange}
                        placeholder = "Enter your password"
                        autoComplete = "off"
                      />
                    </div>
                    <div className = {styles.visibilityIcon} onClick = {()=>setShowPassword(!showPassword)}>{showPassword ? <IoEyeOffOutline/> : <IoEyeOutline/>}</div>
                  </div>
                  {errors.password && <div data-testid = "password-error" className = {styles.inputError}>{errors.password}</div>}
                </div>
                <div className = {styles.inputWrapper}>
                  <label 
                    htmlFor = "confirm_password"
                  >
                    Confirm Password
                  </label>
                  <div className={`${styles.inputBox} ${errors.confirm_password ? styles.errorBox : ""}`}>
                  {errors.confirm_password ? <div className = {styles.errorIcon}><RiErrorWarningFill/></div> : <div className = {styles.inputIcon}><CgPassword/></div>}
                    <div className = {styles.inputContainer}>
                      <input
                        type = "password"
                        value = {data.confirm_password}
                        name = "confirm_password"
                        onChange = {handleChange}
                        placeholder = "Re-type password"
                        autoComplete = "off"
                      />
                    </div>
                  </div>
                  {errors.confirm_password && <div data-testid = "confirm-password-error" className = {styles.inputError}>{errors.confirm_password}</div>}
                </div>
              </div>
              <button type = "submit" className = {loading ? styles.loading : ""} disabled = {isDisabled}>
                Sign up
                <div className = {styles.arrowCircle}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </div>
                {loading && <LoadingButton/>}
              </button>
              <div className = {styles.linkWrapper}>Already have an account?<NavLink to = "/login">Login</NavLink></div>
            </form>
          </main>
      </div>
      }
      floatingContent={<SignUpFloatingCards/>}
    />
  )
}

export default SignUp